import React from "react";
import {
  Card,
  Col,
  DatePicker,
  Empty,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, centsToDollar } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import {
  ItemSchemaOption,
  PrepaidCardSchema,
  PrepaidCardStat,
  PrepaidCardStatsResponse,
} from "./types";

const { RangePicker } = DatePicker;
const { Text } = Typography;

type PrepaidCardStatsViewProps = {
  restaurantId: string;
};

type ItemSchemaGroupResponse = {
  item_schema_groups: Array<{
    item_schemas: Array<{ id: string; name: string }>;
  }>;
};

export const PrepaidCardStatsView: React.FC<PrepaidCardStatsViewProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { notifyError } = useNotification();

  // Default range: last 90 days.
  const defaultStart = dayjs().startOf("day").subtract(89, "days");
  const defaultEnd = dayjs().startOf("day").add(1, "days");

  const [dateRange, setDateRange] = React.useState<[Dayjs, Dayjs]>([defaultStart, defaultEnd]);
  const [stats, setStats] = React.useState<PrepaidCardStat[] | undefined>(undefined);
  const [schemas, setSchemas] = React.useState<PrepaidCardSchema[]>([]);
  const [itemSchemaOptions, setItemSchemaOptions] = React.useState<ItemSchemaOption[]>([]);

  const refreshItemSchemas = () => {
    getFetchT<ItemSchemaGroupResponse>(
      `${API_URL}/v1/r/${restaurantId}/menu`,
      (data) => {
        const allItems = data.item_schema_groups.flatMap((g) => g.item_schemas);
        setItemSchemaOptions(allItems);
      },
      () => {
        // Non-critical: only used to resolve supported item names in the expand row.
      }
    );
  };

  const refreshSchemas = () => {
    getFetchT<PrepaidCardSchema[]>(
      `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_schemas?include_archived=true`,
      (result) => setSchemas(result ?? []),
      () => {
        // Non-critical: only used for the supported-items expand row.
      }
    );
  };

  const refreshStats = () => {
    const [start, end] = dateRange;
    if (!start || !end) return;
    setStats(undefined);
    getFetchT<PrepaidCardStatsResponse>(
      `${API_URL}/v1/r/${restaurantId}/admin/prepaid_card_stats` +
        `?start_time=${encodeURIComponent(start.toISOString())}` +
        `&end_time=${encodeURIComponent(end.toISOString())}`,
      (result) => setStats(result.stats ?? []),
      (err) => {
        notifyError(t("error"), err);
        setStats([]);
      }
    );
  };

  React.useEffect(() => {
    refreshSchemas();
    refreshItemSchemas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  React.useEffect(refreshStats, [restaurantId, dateRange]);

  const supportedItemNames = (schemaId: string): string[] => {
    const schema = schemas.find((s) => s.id === schemaId);
    if (!schema) return [];
    return schema.item_schema_ids.map(
      (id) => itemSchemaOptions.find((o) => o.id === id)?.name ?? id.slice(0, 8)
    );
  };

  const totalOutstanding = React.useMemo(
    () => (stats ?? []).reduce((sum, s) => sum + s.counts_outstanding, 0),
    [stats]
  );
  const totalLiability = React.useMemo(
    () => (stats ?? []).reduce((sum, s) => sum + s.liability, 0),
    [stats]
  );

  const cardStyle = {
    borderRadius: token.borderRadiusLG,
    border: `1px solid ${token.colorBorder}`,
    boxShadow: token.boxShadowTertiary,
  };

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      render: (name: string, s: PrepaidCardStat) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Tag color={s.published ? "green" : "default"}>
            {s.published ? t("prepaid_card_status_live") : t("prepaid_card_status_draft")}
          </Tag>
        </Space>
      ),
    },
    {
      title: t("prepaid_card_outstanding_redeems"),
      dataIndex: "counts_outstanding",
      key: "counts_outstanding",
      render: (v: number) => (
        <Text strong style={{ fontSize: token.fontSizeLG, color: token.colorPrimary }}>
          {v}
        </Text>
      ),
      sorter: (a: PrepaidCardStat, b: PrepaidCardStat) =>
        a.counts_outstanding - b.counts_outstanding,
    },
    {
      title: t("prepaid_card_expired_redeems"),
      dataIndex: "counts_expired",
      key: "counts_expired",
      render: (v: number) => <Text type="secondary">{v}</Text>,
    },
    {
      title: t("prepaid_card_liability"),
      dataIndex: "liability",
      key: "liability",
      render: (v: number) => `$${centsToDollar(v)}`,
      sorter: (a: PrepaidCardStat, b: PrepaidCardStat) => a.liability - b.liability,
    },
    {
      title: t("prepaid_card_cards_sold"),
      dataIndex: "cards_sold",
      key: "cards_sold",
    },
    {
      title: t("prepaid_card_cards_pending_payment"),
      dataIndex: "cards_pending_payment",
      key: "cards_pending_payment",
    },
    {
      title: t("prepaid_card_gross_revenue"),
      dataIndex: "gross_revenue",
      key: "gross_revenue",
      render: (v: number) => `$${centsToDollar(v)}`,
    },
    {
      title: t("prepaid_card_counts_sold"),
      dataIndex: "counts_sold",
      key: "counts_sold",
    },
    {
      title: t("prepaid_card_counts_redeemed"),
      dataIndex: "counts_redeemed",
      key: "counts_redeemed",
    },
    {
      title: t("prepaid_card_counts_reserved"),
      dataIndex: "counts_reserved",
      key: "counts_reserved",
    },
  ];

  return (
    <div>
      <Card
        style={{ ...cardStyle, marginBottom: token.marginXL }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Row gutter={[16, 16]} align="middle" justify="space-between">
          <Col xs={24} md={12}>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="YYYY/MM/DD"
              style={{ width: "100%" }}
              placeholder={[t("start_date"), t("end_date")]}
            />
          </Col>
          <Col xs={24} md={12}>
            <Row gutter={16} justify="end">
              <Col>
                <Statistic
                  title={t("prepaid_card_total_outstanding_redeems")}
                  value={totalOutstanding}
                  valueStyle={{ color: token.colorPrimary }}
                />
              </Col>
              <Col>
                <Statistic
                  title={t("prepaid_card_total_outstanding_liability")}
                  value={`$${centsToDollar(totalLiability)}`}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card>

      <Card
        title={t("prepaid_card_stats")}
        style={cardStyle}
        styles={{ body: { padding: token.paddingLG } }}
      >
        {stats === undefined ? (
          <div style={{ textAlign: "center", padding: token.paddingXL }}>
            <Spin size="large" />
          </div>
        ) : stats.length === 0 ? (
          <Empty description={t("prepaid_card_no_stats_available")} />
        ) : (
          <Table
            rowKey="prepaid_card_schema_id"
            columns={columns}
            dataSource={stats}
            pagination={false}
            size="middle"
            expandable={{
              expandedRowRender: (s) => {
                const names = supportedItemNames(s.prepaid_card_schema_id);
                return names.length === 0 ? (
                  <Text type="secondary">{t("prepaid_card_supported_items_unavailable")}</Text>
                ) : (
                  <Space direction="vertical" size={4}>
                    <Text strong>{t("prepaid_card_supported_items")}</Text>
                    <Space wrap>
                      {names.map((name, i) => (
                        <Tag key={i}>{name}</Tag>
                      ))}
                    </Space>
                  </Space>
                );
              },
            }}
          />
        )}
      </Card>
    </div>
  );
};
